/* ==========================================================================
   Mix & Match — Scroll Story

   Le script fait trois choses, rien de plus :
   1. décider du mode d'affichage (flat ou scroll) ;
   2. pendant le scroll, écrire la progression dans des variables CSS et
      l'étape courante dans data-step ;
   3. ajouter au panier la variante sélectionnée du produit promotionnel.

   Toutes les transformations visuelles sont dans la feuille de style.
   ========================================================================== */

(function () {
  'use strict';

  if (window.MixMatchStory) return;

  var ROOT_SELECTOR = '[data-mms-root]';
  var instances = new Map();

  /* Bornes de chaque étape sur la progression globale (0 → 1). */
  var STEPS = [
    { name: 'intro', start: 0, end: 0.11 },
    { name: 'split', start: 0.11, end: 0.24 },
    { name: 'worlds', start: 0.24, end: 0.42 },
    { name: 'cup', start: 0.42, end: 0.58 },
    { name: 'story', start: 0.58, end: 0.78 },
    { name: 'reveal', start: 0.78, end: 1 }
  ];

  function clamp01(value) {
    if (value < 0) return 0;
    if (value > 1) return 1;
    return value;
  }

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function cartAddUrl() {
    var themeRoutes = window.theme && window.theme.routes;
    if (themeRoutes && themeRoutes.cart_add_url) return themeRoutes.cart_add_url;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'cart/add.js';
  }

  function cartUrl() {
    var themeRoutes = window.theme && window.theme.routes;
    if (themeRoutes && themeRoutes.cart_url) return themeRoutes.cart_url;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'cart';
  }

  /* Le thème expose en général Shopify.formatMoney + Shopify.money_format ;
     à défaut, on retombe sur le format visible partout ailleurs sur le site
     (virgule décimale, symbole après le montant). */
  function formatMoney(cents) {
    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function' &&
      window.Shopify.money_format
    ) {
      try {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format);
      } catch (e) {
        /* on retombe sur le format par défaut ci-dessous */
      }
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  /* ------------------------------------------------------------------ */
  /* Instance                                                            */
  /* ------------------------------------------------------------------ */

  function Instance(root) {
    this.root = root;
    this.track = root.querySelector('[data-mms-track]');
    this.stage = root.querySelector('[data-mms-stage]');
    this.video = root.querySelector('[data-mms-video]');
    this.status = root.querySelector('[data-mms-status]');
    this.revealPanel = root.querySelector('[data-mms-panel="reveal"]');
    this.form = root.querySelector('[data-mms-form]');
    this.stickyBar = root.querySelector('[data-mms-sticky-atc]');
    this.stickyButton = root.querySelector('[data-mms-sticky-submit]');
    this.stickyLabel = root.querySelector('[data-mms-sticky-label]');

    this.written = {};
    this.step = null;
    this.visible = false;
    this.ticking = false;
    this.submitting = false;
    this.listeners = [];

    this.onScroll = this.requestUpdate.bind(this);
    this.onResize = this.requestUpdate.bind(this);

    this.setMode();
    this.bind();
    this.observeStickyAtc();
    this.bindSizePicker();

    if (this.mode === 'scroll') {
      this.observe();
      this.update();
    }
  }

  Instance.prototype.setMode = function () {
    var wantsSticky = this.root.getAttribute('data-mms-sticky') === 'true';
    var wantsAnimation = this.root.getAttribute('data-mms-animate') === 'true';
    var supported =
      'IntersectionObserver' in window &&
      typeof window.CSS !== 'undefined' &&
      typeof window.CSS.supports === 'function' &&
      window.CSS.supports('position', 'sticky');

    this.mode = wantsSticky && wantsAnimation && supported && !prefersReducedMotion() ? 'scroll' : 'flat';
    this.root.setAttribute('data-mms-mode', this.mode);
  };

  Instance.prototype.on = function (target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.listeners.push([target, type, handler, options]);
  };

  Instance.prototype.observe = function () {
    var self = this;

    /* Hors écran, on ne calcule plus rien. */
    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          self.visible = entry.isIntersecting;
          if (self.visible) self.requestUpdate();
        });
      },
      { rootMargin: '10% 0px' }
    );
    this.observer.observe(this.root);

    this.on(window, 'scroll', this.onScroll, { passive: true });
    this.on(window, 'resize', this.onResize, { passive: true });
  };

  /* Barre d'ajout persistante (mobile) : visible dès que le CTA principal est
     sorti de l'écran, une fois qu'il a été vu au moins une fois. En mode
     scroll, le CTA n'est géométriquement hors-champ qu'une fois toute la
     scène pinnée dépassée — ce qui suppose déjà d'avoir défilé jusqu'au
     reveal. La visibilité réelle (CSS) importe peu à IntersectionObserver :
     seule la géométrie compte, ce qui est ici exactement ce qu'on veut. */
  Instance.prototype.observeStickyAtc = function () {
    if (!this.stickyBar || !('IntersectionObserver' in window)) return;

    var ctaEl = this.form && this.form.querySelector('[data-mms-submit]');
    if (!ctaEl) return;

    var self = this;
    var seen = false;

    this.stickyObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) seen = true;
          self.stickyBar.classList.toggle('is-visible', seen && !entry.isIntersecting);
        });
      },
      { threshold: 0.2 }
    );
    this.stickyObserver.observe(ctaEl);

    if (this.stickyButton) {
      this.on(this.stickyButton, 'click', function () {
        if (!self.form) return;
        if (typeof self.form.requestSubmit === 'function') {
          self.form.requestSubmit();
        } else {
          self.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      });
    }
  };

  /* Le produit promotionnel contient ses formats et ses prix. Chaque bouton
     fournit l'ID, le prix actuel et le compare_at_price de sa variante. */
  Instance.prototype.bindSizePicker = function () {
    var picker = this.root.querySelector('[data-mms-size-picker]');
    if (!picker || !this.form) return;

    var mainIdInput = this.form.querySelector('input[name="items[0][id]"]');
    var mainSizeInput = this.form.querySelector('input[name="items[0][properties][_size]"]');

    var priceWas = this.root.querySelector('.mix-match-story__hero-price-was');
    var priceNow = this.root.querySelector('.mix-match-story__hero-price-now');
    var priceBadge = this.root.querySelector('.mix-match-story__hero-badge');
    var ctaLabel = this.form.querySelector('[data-mms-cta-label]');
    var ctaLabelBase = this.form.dataset.ctaLabelBase || (ctaLabel ? ctaLabel.textContent : '');

    var stickyPrice = this.stickyBar ? this.stickyBar.querySelector('.mix-match-story__sticky-atc-price') : null;
    var stickyWas = stickyPrice ? stickyPrice.querySelector('s') : null;
    var stickyNow = stickyPrice ? stickyPrice.querySelector('strong') : null;
    var formatBottles = this.root.querySelectorAll('[data-mms-format-bottle]');
    var buttons = picker.querySelectorAll('[data-mms-size-option]');

    var self = this;

    function applySelection(button) {
      var variantId = button.getAttribute('data-variant-id');
      var price = Number(button.getAttribute('data-price'));
      var compareAtPrice = Number(button.getAttribute('data-compare-at-price') || 0);
      var title = button.getAttribute('data-title');
      var format = button.getAttribute('data-format');

      if (mainIdInput) mainIdInput.value = variantId;
      if (mainSizeInput) mainSizeInput.value = title;

      if (priceNow) {
        priceNow.hidden = price <= 0;
        priceNow.textContent = price > 0 ? formatMoney(price) : '';
      }
      if (priceWas) {
        priceWas.hidden = compareAtPrice <= price;
        priceWas.textContent = compareAtPrice > price ? formatMoney(compareAtPrice) : '';
      }
      if (priceBadge) {
        priceBadge.hidden = compareAtPrice <= price;
      }
      if (priceBadge && compareAtPrice > price) {
        var percent = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
        priceBadge.textContent = '−' + percent + '%';
      }
      if (stickyNow) {
        stickyNow.hidden = price <= 0;
        stickyNow.textContent = price > 0 ? formatMoney(price) : '';
      }
      if (stickyWas) {
        stickyWas.hidden = compareAtPrice <= price;
        stickyWas.textContent = compareAtPrice > price ? formatMoney(compareAtPrice) : '';
      }
      if (ctaLabel && !self.submitting) {
        ctaLabel.textContent = price > 0 ? ctaLabelBase + ' — ' + formatMoney(price) : ctaLabelBase;
      }

      Array.prototype.forEach.call(formatBottles, function (bottle) {
        bottle.hidden = bottle.getAttribute('data-mms-format-bottle') !== format;
      });

      Array.prototype.forEach.call(buttons, function (btn) {
        var active = btn === button;
        btn.classList.toggle('is-selected', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    this.on(picker, 'click', function (event) {
      var button = event.target.closest ? event.target.closest('[data-mms-size-option]') : null;
      if (!button || button.disabled || button.classList.contains('is-selected')) return;
      applySelection(button);
    });

    /* Le prix, le prix barré et la disponibilité viennent déjà de Liquid à
       chaque chargement de page (SSR) — pas besoin de les re-vérifier en
       JS après coup. */
    var selected = picker.querySelector('[data-mms-size-option].is-selected') || buttons[0];
    if (selected) applySelection(selected);
  };

  Instance.prototype.requestUpdate = function () {
    if (this.ticking || !this.visible) return;
    this.ticking = true;

    var self = this;
    window.requestAnimationFrame(function () {
      self.ticking = false;
      self.update();
    });
  };

  Instance.prototype.update = function () {
    if (this.mode !== 'scroll' || !this.track || !this.stage) return;

    var scrollable = this.track.offsetHeight - this.stage.offsetHeight;
    if (scrollable <= 0) return;

    var progress = clamp01(-this.track.getBoundingClientRect().top / scrollable);
    this.write('--mms-p', progress);

    var current = STEPS[0].name;

    for (var i = 0; i < STEPS.length; i++) {
      var step = STEPS[i];
      var sub = clamp01((progress - step.start) / (step.end - step.start));
      this.write('--mms-p-' + step.name, sub);
      if (progress >= step.start) current = step.name;
    }

    if (current !== this.step) {
      this.step = current;
      this.root.setAttribute('data-step', current);
      this.toggleInteractive();
      this.toggleVideo();
    }
  };

  /* Une variable CSS n'est réécrite que si elle a bougé de façon perceptible. */
  Instance.prototype.write = function (name, value) {
    var rounded = Math.round(value * 1000) / 1000;
    if (this.written[name] === rounded) return;
    this.written[name] = rounded;
    this.root.style.setProperty(name, String(rounded));
  };

  /* Le panneau masqué ne doit pas piéger le focus clavier. */
  Instance.prototype.toggleInteractive = function () {
    if (this.revealPanel) this.revealPanel.toggleAttribute('data-mms-hidden', this.step !== 'reveal');
  };

  Instance.prototype.toggleVideo = function () {
    if (!this.video) return;

    if (this.step === 'cup' || this.step === 'story') {
      var playing = this.video.play();
      if (playing && typeof playing.catch === 'function') playing.catch(function () {});
    } else if (!this.video.paused) {
      this.video.pause();
    }
  };

  /* ------------------------------------------------------------------ */
  /* Ajout au panier                                                     */
  /* ------------------------------------------------------------------ */

  Instance.prototype.bind = function () {
    if (!this.form) return;

    var self = this;
    this.on(this.form, 'submit', function (event) {
      self.submit(event);
    });
  };

  function collectItems(form) {
    var items = [];
    var inputs = form.querySelectorAll('input[name^="items["]');
    var pattern = /^items\[(\d+)\]\[(\w+)\](?:\[([^\]]+)\])?$/;

    Array.prototype.forEach.call(inputs, function (input) {
      var match = pattern.exec(input.name);
      if (!match) return;

      var position = Number(match[1]);
      var key = match[2];
      var subKey = match[3];

      items[position] = items[position] || {};

      if (key === 'properties' && subKey) {
        items[position].properties = items[position].properties || {};
        items[position].properties[subKey] = input.value;
      } else if (key === 'id' || key === 'quantity') {
        items[position][key] = Number(input.value);
      }
    });

    return items.filter(function (item) {
      return item && item.id;
    });
  }

  Instance.prototype.setStatus = function (message, state) {
    if (!this.status) return;
    this.status.textContent = message || '';
    if (state) {
      this.status.setAttribute('data-state', state);
    } else {
      this.status.removeAttribute('data-state');
    }
  };

  /* Les deux CTA (hero + barre sticky) restent synchronisés : même état
     occupé/succès, pour qu'aucun des deux ne mente sur ce qui se passe. */
  Instance.prototype.setButtonsBusy = function (busy) {
    [this.mainButton, this.stickyButton].forEach(function (btn) {
      if (!btn) return;
      btn.disabled = busy;
      if (busy) {
        btn.setAttribute('aria-busy', 'true');
      } else {
        btn.removeAttribute('aria-busy');
      }
    });
  };

  Instance.prototype.setButtonsSuccess = function (isSuccess) {
    [this.mainButton, this.stickyButton].forEach(function (btn) {
      if (btn) btn.classList.toggle('is-success', isSuccess);
    });
  };

  Instance.prototype.setLabels = function (text) {
    if (this.mainLabel) this.mainLabel.textContent = text;
  };

  /* La barre sticky garde son propre libellé (prix inclus) : on ne le
     remplace que pendant les états occupé/succès, jamais autrement. */
  Instance.prototype.setStickyLabel = function (text) {
    if (this.stickyLabel) this.stickyLabel.textContent = text;
  };

  Instance.prototype.submit = function (event) {
    event.preventDefault();

    if (this.submitting) return;

    var form = this.form;
    var items = collectItems(form);
    if (!items.length) return;

    this.mainButton = form.querySelector('[data-mms-submit]');
    this.mainLabel = form.querySelector('[data-mms-cta-label]');

    var initialLabel = this.mainLabel ? this.mainLabel.textContent : '';
    var initialStickyLabel = this.stickyLabel ? this.stickyLabel.textContent : '';
    var settings = this.root.dataset;
    var self = this;

    this.submitting = true;
    this.setButtonsBusy(true);
    this.setLabels(settings.loadingLabel || initialLabel);
    this.setStickyLabel(settings.loadingLabel || initialStickyLabel);
    this.setStatus('', null);

    /* Le thème indique lui-même les sections à rafraîchir. */
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
    );

    fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: sections,
        sections_url: window.location.pathname
      })
    })
      .then(function (response) {
        return response.json().then(function (state) {
          if (!response.ok || state.status) {
            throw new Error(state.description || state.message || settings.errorMessage);
          }
          return state;
        });
      })
      .then(function (state) {
        return self.refreshCart(state);
      })
      .then(function (refreshed) {
        self.setStatus(settings.successMessage, 'success');

        /* Confirmation visible ~700 ms avant l'ouverture du panier : le temps
           que « ✓ Mix ajouté » soit lu, sans ralentir la conversion. */
        self.setButtonsBusy(false);
        self.setButtonsSuccess(true);
        self.setLabels(settings.successLabel || initialLabel);
        self.setStickyLabel(settings.successLabel || initialStickyLabel);

        window.setTimeout(function () {
          self.openCart(self.mainButton, refreshed);

          window.setTimeout(function () {
            self.setButtonsSuccess(false);
            self.setLabels(initialLabel);
            self.setStickyLabel(initialStickyLabel);
            self.submitting = false;
          }, 250);
        }, 650);
      })
      .catch(function (error) {
        self.setStatus(error.message || settings.errorMessage, 'error');
        self.setButtonsBusy(false);
        self.setLabels(initialLabel);
        self.setStickyLabel(initialStickyLabel);
        self.submitting = false;
      });
  };

  /* Résout à true seulement si le panier du thème a réellement été rafraîchi. */
  Instance.prototype.refreshCart = function (state) {
    var pubsub = window.theme && window.theme.pubsub;
    if (!pubsub || !pubsub.PUB_SUB_EVENTS || !pubsub.PUB_SUB_EVENTS.cartUpdate) return Promise.resolve(false);

    return fetch(cartUrl(), { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        cart.sections = state.sections;
        pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'mix-match-story', cart: cart });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: state } }));
        return true;
      })
      .catch(function () {
        return false;
      });
  };

  /* Sans rafraîchissement, le drawer afficherait un panier périmé : on bascule
     alors sur la page panier. */
  Instance.prototype.openCart = function (activeElement, refreshed) {
    var drawer = document.querySelector('cart-drawer');

    if (refreshed && drawer && typeof drawer.show === 'function') {
      drawer.show(activeElement || null);
      return;
    }

    window.location.href = cartUrl();
  };

  /* ------------------------------------------------------------------ */
  /* Cycle de vie                                                        */
  /* ------------------------------------------------------------------ */

  Instance.prototype.destroy = function () {
    if (this.observer) this.observer.disconnect();
    if (this.stickyObserver) this.stickyObserver.disconnect();

    this.listeners.forEach(function (entry) {
      entry[0].removeEventListener(entry[1], entry[2], entry[3]);
    });
    this.listeners.length = 0;
  };

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = [];

    if (context.matches && context.matches(ROOT_SELECTOR)) roots.push(context);
    Array.prototype.push.apply(roots, context.querySelectorAll(ROOT_SELECTOR));

    roots.forEach(function (root) {
      if (instances.has(root)) return;
      instances.set(root, new Instance(root));
    });
  }

  function destroy(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;

    instances.forEach(function (instance, root) {
      if (context !== document && context !== root && !context.contains(root)) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  window.MixMatchStory = { init: init, destroy: destroy, instances: instances };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });
})();
